import { getDuaPageAndPartKey } from '@components/DuaPage/duaPartHelper'
import { contentLibrary } from '@components/shared/consts'
import LoadingSpinner from '@components/shared/Loading/Loading'
import { useUserData } from '@contexts/UserDataContext'
import { useNativeReaderLink } from '@hooks/useNativeReaderLink'
import Link from 'next/link'
import { FaBookmark, FaCog, FaHistory } from 'react-icons/fa'
import SectionWrapper from '../common/SectionWrapper'

/** Shows resume and bookmark links using the appropriate web or native reader. */
const MarkedPositions = () => {
  const getReaderLinkProps = useNativeReaderLink()
  const { userData, loading } = useUserData()

  const activeItems = contentLibrary.filter(
    (item) => userData?.homePageButtons?.[item.type],
  )

  return (
    <SectionWrapper>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-linear-to-br from-emerald-500 to-teal-600 shadow-md">
            <FaBookmark className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-main">Kalınan Yerler</h3>
        </div>
        <Link
          href="/hesabim?tab=homeConfig"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-muted bg-surface-subtle hover:bg-surface-strong transition-all duration-200"
        >
          <FaCog className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Düzenle</span>
        </Link>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner fullScreen={false} />
        </div>
      ) : activeItems.length === 0 ? (
        <div className="text-center py-10 px-4 rounded-xl bg-surface-subtle border border-dashed border-main">
          <FaBookmark className="w-8 h-8 mx-auto mb-3 text-muted opacity-50" />
          <p className="text-muted text-sm">Henüz kalınan yer eklenmedi</p>
          <Link
            href="/hesabim?tab=homeConfig"
            className="inline-block mt-3 text-sm font-medium text-brand-secondary hover:underline"
          >
            Ayarlardan ekleyin →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {contentLibrary.map((item) => {
            if (!userData?.homePageButtons?.[item.type]) return null

            const bookmark = userData[item.type]
            const lastRead = userData.lastReadPages?.[item.type]

            const [bookmarkedPage, bookmarkedPart] = getDuaPageAndPartKey(bookmark)
            const [lastReadPageKey, lastReadPart] = getDuaPageAndPartKey(lastRead ?? null)

            const hasBookmark = !!(bookmark && bookmark > 0)
            const hasLastRead = !!(lastRead && lastRead > 1)

            const different = !!(hasBookmark && hasLastRead && bookmark !== lastRead)

            const primaryPage = (hasLastRead ? lastReadPageKey : bookmarkedPage) || 1
            const primaryPart = hasLastRead ? lastReadPart : bookmarkedPart

            const resumeHref = `${item.basePath || ''}/${item.type}/${primaryPage}${primaryPart ? `#bolum-${primaryPart}` : ''}`
            const bookmarkHref = `${item.basePath || ''}/${item.type}/${bookmarkedPage || 1}${bookmarkedPart ? `#bolum-${bookmarkedPart}` : ''}`

            return (
              <div
                key={item.type}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 shadow-xs hover:shadow-md transition-all duration-300"
              >
                {/* Visual Accent */}
                <div
                  className="absolute top-0 left-0 right-0 h-1.5 opacity-80"
                  style={{ backgroundColor: item.color }}
                />

                <div className="flex flex-col h-full">
                  {/* Title Area - Direct Link to Last/Primary */}
                  <Link
                    {...getReaderLinkProps(resumeHref)}
                    className="flex-1 p-3 md:p-4 active:bg-slate-50 dark:active:bg-slate-900/50 transition-colors"
                  >
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      {item.name}
                    </h4>

                    <div className="mt-2.5 space-y-1">
                      {hasLastRead && different ? (
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                          <FaHistory className="w-2.5 h-2.5" />
                          <span>Son açılan: {lastReadPageKey}. Sayfa</span>
                        </div>
                      ) : hasBookmark ? (
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-blue-600 dark:text-blue-400">
                          <FaBookmark className="w-2.5 h-2.5" />
                          <span>İşaretli: {bookmarkedPage}. Sayfa</span>
                        </div>
                      ) : hasLastRead ? (
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                          <FaHistory className="w-2.5 h-2.5" />
                          <span>Son açılan: {lastReadPageKey}. Sayfa</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-400">
                          1. Sayfadan başla
                        </span>
                      )}
                    </div>
                  </Link>

                  {/* Secondary Action Bar (Only if different) */}
                  {different && (
                    <div className="border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/20">
                      <Link
                        {...getReaderLinkProps(bookmarkHref)}
                        className="flex items-center justify-center gap-1.5 py-2.5 text-[10px] font-bold text-slate-500 hover:text-primary-600 dark:text-slate-400 transition-colors"
                      >
                        <FaBookmark className="w-2.5 h-2.5" />
                        İşaretliye Git ({bookmarkedPage})
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </SectionWrapper>
  )
}

export default MarkedPositions
